import { useContext } from "react";
import { Dropdown } from "grabcad-ui-elements";
import { GroupVersionChangeIndicator } from "./GroupVersionChangeIndicator";
import { Group, SoftwareTagWithUpdateStrategy } from "../types/version_management_types";
import { UpdateStrategy } from "@grabcad/company-server-shared/dist/types";
import { ApplicationContext } from "../../../components/ApplicationProvider";
import { useTagDropDownOptions } from "../hooks/useTagDropDownOptions";

export interface VersionManagementGroupsTableProps {
    groups?: Group[];
    onGroupVersionChange: (
        groupId: number,
        nextVersion: undefined | null | { updateStrategy: UpdateStrategy; tagId: number }
    ) => void;
    softwareTags?: SoftwareTagWithUpdateStrategy[];
    isReadOnly?: boolean;
}

export const DEFAULT_TAG_VALUE = 0; // sentinel for "no tag / default"

function getSelectedTagId(group: Group) {
    // If the next version is set, return its tag id. If the next version is null, return the default tag value.
    if (group.nextVersion || group.nextVersion === null) {
        return group.nextVersion === null ? DEFAULT_TAG_VALUE : group.nextVersion?.tagId;
    }

    // Return the current tag id. If there is no current version, return the default tag value.
    return !group.currentVersion ? DEFAULT_TAG_VALUE : group.currentTagId;
}

export function VersionManagementGroupsTable({
    groups = [],
    onGroupVersionChange,
    softwareTags = [],
    isReadOnly,
}: VersionManagementGroupsTableProps) {
    const { t } = useContext(ApplicationContext);
    const SAME_AS_COMPANY_SELECT_OPTION = t(
        "version_management.groups.same_as_company_select_option"
    );

    const dropdownOptions = useTagDropDownOptions(softwareTags, {
        prependOptions: [{ value: DEFAULT_TAG_VALUE, text: SAME_AS_COMPANY_SELECT_OPTION }],
    });

    const handleVersionChange = (groupId: number, tagId?: number) => {
        if (isReadOnly) {
            return;
        }

        const group = groups.find((g) => g.id === groupId);

        if (!group) {
            return;
        }

        if (group.currentTagId === tagId) {
            onGroupVersionChange(groupId, undefined);
        } else {
            const updateStrategy = softwareTags?.find((tag) => tag.id === tagId)?.updateStrategy;
            onGroupVersionChange(
                groupId,
                updateStrategy && tagId ? { updateStrategy, tagId } : null
            );
        }
    };

    return (
        <table className="ui table" aria-label="Groups">
            <thead>
                <tr>
                    <th>{t("version_management.table_headings.group_name")}</th>
                    <th>{t("version_management.table_headings.stream")}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {groups.map((group) => (
                    <tr key={group.id}>
                        <td className="five wide">{group.name}</td>
                        <td className="six wide">
                            <Dropdown
                                options={dropdownOptions}
                                placeholder=""
                                value={getSelectedTagId(group)}
                                fluid
                                onChange={(event, data) => {
                                    handleVersionChange(group.id, data.value as number);
                                }}
                            />
                        </td>
                        <td className="five wide right aligned" role="version">
                            <GroupVersionChangeIndicator
                                currentVersion={group.currentVersion}
                                currentTagId={group.currentTagId}
                                nextVersion={group.nextVersion}
                            />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
